import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateLong } from '../utils/dateUtils';
import { 
  Box, 
  Typography, 
  Paper, 
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material';
import StatusBadge from '../components/orders/StatusBadge';
import WorkflowActions from '../components/orders/WorkflowActions';
import WorkflowHistory from '../components/orders/WorkflowHistory';
import RevisionHistory from '../components/RevisionHistory';
import useAdOrders from '../hooks/useAdOrders';
import useOrderSync from '../hooks/useOrderSync';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';
import OrderDetail from '../components/OrderDetail';
import { 
  NoteAdd as NoteAddIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

// Helper function to parse client revision requests from design notes (Order Workflow version)
const parseClientRevisionsWorkflow = (designNotes) => {
  if (!designNotes) return { cleanDesignNotes: '', clientRevisions: [] };
  
  const revisionSeparator = '?? === CLIENT REVISION REQUEST ===';
  const parts = designNotes.split(revisionSeparator);
  
  // First part is the original design notes (before any revisions)
  const cleanDesignNotes = parts[0].trim();
  
  // Parse each revision request
  const clientRevisions = [];
  for (let i = 1; i < parts.length; i++) {
    const revisionText = parts[i];
    
    // Extract date and feedback using regex
    const dateMatch = revisionText.match(/Date: ([^\n]+)/);
    const feedbackMatch = revisionText.match(/Client Feedback: ([^\n]+)/);
    
    if (dateMatch && feedbackMatch) {
      const dateStr = dateMatch[1];
      const feedback = feedbackMatch[1];
      
      // Format the date to match the desired format (dd/mm/yy, hh:mm am/pm)
      try {
        const date = new Date(dateStr.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }) + ', ' + date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }).toLowerCase();
        
        clientRevisions.push({
          feedback,
          date: formattedDate,
          originalDate: dateStr
        });
      } catch (error) {
        // If date parsing fails, use original format
        clientRevisions.push({
          feedback,
          date: dateStr,
          originalDate: dateStr
        });
      }
    }
  }
  
  return { cleanDesignNotes, clientRevisions };
};

// EmbeddedNotes Component - Display notes directly in order details (Order Workflow version)
const EmbeddedNotesWorkflow = ({ order }) => {
  const notesData = [];
  
  // Parse client revisions from design notes
  const { cleanDesignNotes, clientRevisions } = parseClientRevisionsWorkflow(order.designNotes);
  
  // Collect notes based on order data
  if (order.notes) {
    notesData.push({
      type: 'Sales Notes',
      content: order.notes,
      color: 'primary.main',
      bgColor: 'blue.50',
      borderColor: 'blue.200'
    });
  }
  
  // Use cleaned design notes (without client revision requests)
  if (cleanDesignNotes) {
    notesData.push({
      type: 'Design Notes', 
      content: cleanDesignNotes,
      color: 'secondary.main',
      bgColor: 'purple.50',
      borderColor: 'purple.200'
    });
  }
  
  // Add client notes section if there are any client revisions
  if (clientRevisions.length > 0) {
    const clientNotesContent = clientRevisions.map(revision => 
      `${revision.feedback} (${revision.date})`
    ).join('\n\n');
    
    notesData.push({
      type: 'Client Notes',
      content: clientNotesContent,
      color: 'warning.main',
      bgColor: 'orange.50',
      borderColor: 'orange.200'
    });
  }
  
  if (order.customerNotes) {
    notesData.push({
      type: 'Customer Notes',
      content: order.customerNotes,
      color: 'success.main', 
      bgColor: 'green.50',
      borderColor: 'green.200'
    });
  }
  
  if (notesData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2, px: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No notes available for this order.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {notesData.map((noteData, index) => (
        <Box key={index} sx={{ mb: index < notesData.length - 1 ? 2 : 0 }}>
          <Typography 
            variant="subtitle2" 
            color={noteData.color}
            fontWeight="bold" 
            gutterBottom 
            display="block"
          >
            {noteData.type}:
          </Typography>
          <Paper sx={{ 
            p: 1.5, 
            bgcolor: noteData.bgColor, 
            border: '1px solid', 
            borderColor: noteData.borderColor,
            borderRadius: 1
          }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {noteData.content}
            </Typography>
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

  // Helper function to determine product type when not explicitly set
  const determineProductType = (order) => {
    // If notes indicate order was created from offer or enquiry, it's a Lead
    const notes = order.notes || '';
    if (notes.toLowerCase().includes('order created from offer') || 
        notes.toLowerCase().includes('order from enquiry') ||
        notes.toLowerCase().includes('created from offer')) {
      return 'Lead';
    }
    
    // Check if this order has minimal data (typical of leads)
    const hasMinimalData = !order.startDate && !order.endDate && (!order.audiences || order.audiences.length === 0);
    if (hasMinimalData) {
      return 'Lead';
    }
    
    // Default fallback
    return '';
  };

  // Helper function to format update order type for display
  const formatUpdateOrderType = (order) => {
    // If it's a Header Update, return that
    if (order.updateType === 'Header Update') {
      return 'Header Update';
    }
    
    // For Order Updates, use the specific orderUpdateType if available
    if (order.orderUpdateType) {
      // orderUpdateType can be like "Dates", "Link", "Artwork", "Dates, Link", etc.
      const types = order.orderUpdateType.split(',').map(t => t.trim());
      const formattedTypes = types.map(type => {
        switch (type.toLowerCase()) {
          case 'dates':
            return 'End Date Update';
          case 'link':
            return 'eLink Update';
          case 'artwork':
            return 'Artwork Update';
          default:
            return type;
        }
      });
      return formattedTypes.join(', ');
    }
    
    // Fallback to updateType
    return order.updateType || 'Order Update';
  };

  const OrderWorkflow = () => {
  const { updateOrderStatus } = useAdOrders();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [adOrders, setAdOrders] = useState([]);
  const [updateOrders, setUpdateOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  
  // Tab state - persist to localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('orderWorkflowActiveTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  
  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'organization'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });


  // CancelInvoiceMenuItem � shown in dropdown only when invoice is active
  const CancelInvoiceMenuItem = ({ orderId, onClose }) => {
    const [cancelling, setCancelling] = useState(false);

    const handleCancelInvoice = async () => {
      if (!window.confirm('Are you sure you want to cancel this invoice? This will reset the order status to "Approved" and make the invoice link inactive.')) return;
      try {
        setCancelling(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(`${config.apiUrl}/proof/cancel-invoice/${orderId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setLoading(true);
          fetchOrders();
          if (onClose) onClose();
        }
      } catch (error) {
        console.error('Error cancelling invoice:', error);
      } finally {
        setCancelling(false);
      }
    };

    return (
      <MenuItem onClick={handleCancelInvoice} disabled={cancelling}>
        <ListItemText sx={{ color: 'error.main' }}>
          {cancelling ? 'Cancelling...' : 'Cancel Invoice'}
        </ListItemText>
      </MenuItem>
    );
  };

  // CancelProofMenuItem � shown in dropdown only when proof is active
  const CancelProofMenuItem = ({ orderId, onClose }) => {
    const [cancelling, setCancelling] = useState(false);

    const handleCancelProof = async () => {
      if (!window.confirm('Are you sure you want to cancel this proof? This will reset the order status to "In Design" and make the proof link inactive.')) return;
      try {
        setCancelling(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(`${config.apiUrl}/api/proof/cancel/${orderId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setLoading(true);
          fetchOrders();
          if (onClose) onClose();
        }
      } catch (error) {
        console.error('Error cancelling proof:', error);
      } finally {
        setCancelling(false);
      }
    };

    return (
      <MenuItem onClick={handleCancelProof} disabled={cancelling}>
        <ListItemText sx={{ color: 'error.main' }}>
          {cancelling ? 'Cancelling...' : 'Cancel Proof'}
        </ListItemText>
      </MenuItem>
    );
  };

  // GenerateInvoiceMenuItem � shown for invoiceLater ad orders sitting at "Proof Accepted"
  const GenerateInvoiceMenuItem = ({ order, onClose }) => {
    const [generating, setGenerating] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const invoiceDate = order.invoiceDate ? new Date(order.invoiceDate) : null;
    if (invoiceDate) invoiceDate.setHours(0, 0, 0, 0);
    const dateReached = !invoiceDate || invoiceDate <= today;
    const formattedDate = invoiceDate
      ? invoiceDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
      : null;

    const handleGenerateInvoice = async () => {
      if (!window.confirm('Generate invoice for this order now? This will set the order status to Invoiced.')) return;
      try {
        setGenerating(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${config.apiUrl}/api/ad-orders/${order.id}/generate-invoice`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          fetchOrders();
          if (onClose) onClose();
          // Redirect to Xero invoice if one was created
          if (response.data.invoiceUrl) {
            window.open(response.data.invoiceUrl, '_blank');
          } else if (response.data.xeroError) {
            // Show the Xero error clearly so the user knows what to fix
            alert(`Order marked as Invoiced, but Xero invoice was not created:\n\n${response.data.xeroError}`);
          }
        }
      } catch (error) {
        console.error('Error generating invoice:', error);
        const msg = error.response?.data?.message || 'Failed to generate invoice. Please try again.';
        alert(msg);
      } finally {
        setGenerating(false);
      }
    };

    return (
      <Tooltip
        title={!dateReached && formattedDate ? `Invoice date: ${formattedDate}` : ''}
        placement="left"
      >
        <span>
          <MenuItem onClick={handleGenerateInvoice} disabled={generating || !dateReached}>
            <ListItemIcon>
              <ReceiptIcon fontSize="small" color={dateReached ? 'success' : 'disabled'} />
            </ListItemIcon>
            <ListItemText sx={{ color: dateReached ? 'success.main' : 'text.disabled' }}>
              {generating ? 'Generating...' : dateReached ? 'Generate Invoice' : `Generate Invoice (${formattedDate})`}
            </ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
    );
  };

  // Actions Menu Component - Consolidates all actions into a dropdown menu
  const ActionsMenu = ({ order }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const isStationeryOrder = order.orderType === 'stationery';
    const [menuPosition, setMenuPosition] = useState({ vertical: 'bottom', horizontal: 'right' });

    const handleClick = (event) => {
      const button = event.currentTarget;
      const buttonRect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Check if there's enough space below (need ~400px for full menu)
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // If not enough space below but more space above, open upward
      if (spaceBelow < 400 && spaceAbove > spaceBelow) {
        setMenuPosition({ vertical: 'top', horizontal: 'right' });
      } else {
        setMenuPosition({ vertical: 'bottom', horizontal: 'right' });
      }
      
      setAnchorEl(button);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleViewDetails = () => {
      handleClose();
      // Open the order details dialog instead of navigating
      setSelectedOrder(order);
      setDetailDialogOpen(true);
    };

    const handleOpenNotes = () => {
      handleClose();
      const existingNotes = [];
      if (order.notes) {
        existingNotes.push({
          id: 'general',
          content: order.notes,
          createdBy: 'Order Team',
          createdAt: order.createdAt || new Date().toISOString(),
          isNew: false,
          noteType: 'general'
        });
      }
      if (order.designNotes) {
        existingNotes.push({
          id: 'design',
          content: order.designNotes,
          createdBy: 'Design Team',
          createdAt: order.createdAt || new Date().toISOString(),
          isNew: false,
          noteType: 'design'
        });
      }
      handleOpenNotesDialog('order', order.id, `Order #${order.id} - ${order.organizationName || order.businessName || order.customerName || 'Unknown'}`, existingNotes);
    };

    const totalNoteCount = getTotalNoteCount('order', order);

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ 
            color: 'primary.main',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: menuPosition.vertical,
            horizontal: menuPosition.horizontal,
          }}
          transformOrigin={{
            vertical: menuPosition.vertical === 'bottom' ? 'top' : 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { 
              minWidth: 200,
              maxHeight: 'calc(100vh - 100px)', // Prevent menu from going off screen
              overflow: 'auto' // Enable scrolling if menu is too tall
            }
          }}
        >
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenNotes}>
            <ListItemIcon>
              <NoteAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Notes {totalNoteCount > 0 && `(${totalNoteCount})`}
            </ListItemText>
          </MenuItem>
          <Divider />
          {/* Generate Invoice - for invoiceLater orders at Proof Accepted, or where the
              automatic Xero invoice failed and the order was reverted to "Approved" */}
          {order.orderType === 'ad' &&
            (!!order.invoiceLater || (order.status && order.status.toLowerCase() === 'approved')) &&
            !['uploaded', 'complete', 'archived', 'cancelled'].includes(order.status && order.status.toLowerCase()) &&
            !order.xero_invoice_url && (
            <GenerateInvoiceMenuItem order={order} onClose={handleClose} />
          )}
          {/* View Xero Invoice - shown whenever a Xero invoice URL is available */}
          {order.xero_invoice_url && (
            <MenuItem onClick={() => { window.open(order.xero_invoice_url, '_blank'); handleClose(); }}>
              <ListItemIcon>
                <OpenInNewIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'primary.main' }}>View Xero Invoice</ListItemText>
            </MenuItem>
          )}
          {/* Cancel Invoice � only when invoice is active */}
          {order.status?.toLowerCase() === 'invoiced' && (
            <CancelInvoiceMenuItem orderId={order.id} onClose={handleClose} />
          )}
          {/* Cancel Proof � only when proof is active (not accepted) */}
          {order.status?.toLowerCase() === 'proof active' && (
            <CancelProofMenuItem orderId={order.id} onClose={handleClose} />
          )}
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Status Actions
            </Typography>
            <Box sx={{ mt: 1 }}>
              {isStationeryOrder ? (
                <StationeryOrderActions order={order} />
              ) : (
                <WorkflowActions 
                  order={order} 
                  onStatusChange={(newStatus, notes) => handleStatusChange(order.id, newStatus, notes)}
                  onCreateProof={handleCreateProof}
                  onCreateQuote={handleCreateQuote}
                />
              )}
            </Box>
          </Box>
        </Menu>
      </>
    );
  };

  // Set up real-time order updates via Socket.IO
  // TODO: Update useOrderSync to handle separated orders
  // useOrderSync({
  //   orders: currentTab === 0 ? adOrders : stationeryOrders,
  //   setOrders: currentTab === 0 ? setAdOrders : setStationeryOrders,
  //   selectedOrder,
  //   setSelectedOrder
  // });

  // Fetch orders from ad and stationery types only (excludes school orders)
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();

      let allOrders = [];

      // Fetch ad orders
      try {
        const adResponse = await axios.get(`${config.apiUrl}/api/ad-orders`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (adResponse.data && adResponse.data.success && Array.isArray(adResponse.data.data)) {
          allOrders = [...allOrders, ...adResponse.data.data];
        } else {
          // Try mock endpoint for ad orders
          try {
            const mockResponse = await axios.get(`${config.apiUrl}/api/ad-orders/mock`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            
            if (mockResponse.data && mockResponse.data.success && Array.isArray(mockResponse.data.data)) {
              allOrders = [...allOrders, ...mockResponse.data.data];
            }
          } catch (mockError) {
            console.warn('Mock ad orders endpoint failed:', mockError);
          }
        }
      } catch (adError) {
        console.warn('Error fetching ad orders:', adError);
      }

      // Note: School orders are intentionally excluded from Ad Orders
      // They have their own dedicated School Workflow page
      // Note: Stationery orders are intentionally excluded from Ad Orders
      // They have their own dedicated Stationery Orders page

      // Filter to only ad orders — exclude Cancelled, Uploaded, and Paid
      // Uploaded/Paid orders move to the Finance > Unpaid Orders tab
      const adOrdersList = allOrders.filter(order => {
        const status = order.status?.toLowerCase();
        if (status === 'cancelled') return false;
        if (status === 'uploaded') return false;
        if (status === 'paid') return false;
        // Only include ad orders (orders with audiences)
        return order.audiences && order.audiences.length > 0;
      });
      setAdOrders(adOrdersList);

      // Fetch update orders
      try {
        const updateResponse = await axios.get(`${config.apiUrl}/api/update-orders`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (updateResponse.data && updateResponse.data.success && Array.isArray(updateResponse.data.data)) {
          // Filter out Cancelled update orders (Invoiced stays visible until Uploaded)
          const updateOrdersList = updateResponse.data.data.filter(order => {
            const status = order.status?.toLowerCase();
            return status !== 'cancelled';
          });
          setUpdateOrders(updateOrdersList);
        } else {
          setUpdateOrders([]);
        }
      } catch (updateError) {
        console.warn('Error fetching update orders:', updateError);
        setUpdateOrders([]);
      }
      
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to fetch orders');
      setAdOrders([]);
      setUpdateOrders([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Filter and sort functions
  const filterOrders = (orders) => {
    let filtered = [...orders];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => {
        const orgName = (order.organizationName || order.businessName || '').toLowerCase();
        const orderId = order.id?.toString() || '';
        return orgName.includes(query) || orderId.includes(query);
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => {
        const status = (order.status || '').toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }
    
    return filtered;
  };

  const sortOrders = (orders) => {
    const sorted = [...orders];
    
    sorted.sort((a, b) => {
      let compareValue = 0;
      
      if (sortBy === 'date') {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        compareValue = dateA - dateB;
      } else if (sortBy === 'organization') {
        const nameA = (a.organizationName || a.businessName || '').toLowerCase();
        const nameB = (b.organizationName || b.businessName || '').toLowerCase();
        compareValue = nameA.localeCompare(nameB);
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
    
    return sorted;
  };

  // Get filtered and sorted orders
  const getProcessedOrders = (orders) => {
    return sortOrders(filterOrders(orders));
  };

  // Get unique statuses for filter dropdown
  const getUniqueStatuses = (orders) => {
    const statuses = new Set();
    orders.forEach(order => {
      if (order.status) {
        statuses.add(order.status);
      }
    });
    return Array.from(statuses).sort();
  };

  // Using formatDateLong from dateUtils for consistent DD/MM/YYYY formatting

  // Extract channels from order audiences
  const getChannels = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      // Get unique channels from all audiences
      const channels = [...new Set(order.audiences
        .filter(audience => audience.channel)
        .map(audience => audience.channel))];
      
      if (channels.length > 0) {
        return channels.join(', ');
      }
    }
    
    // Fall back to N/A if no channels found
    return 'N/A';
  };

  // Calculate total price from audiences
  const getTotalPrice = (order) => {
    let total = 0;
    
    // Try to get from audiences first
    if (order.audiences && Array.isArray(order.audiences)) {
      total = order.audiences.reduce((sum, audience) => {
        const price = parseFloat(audience.price || 0);
        const discount = parseFloat(audience.discount || 0);
        // Calculate subtotal accounting for discount
        const subtotal = Math.max(0, price - discount);
        return sum + subtotal;
      }, 0);
      
      if (total > 0) {
        return total.toFixed(2);
      }
    }
    
    // Fall back to direct value field or totalPrice
    return parseFloat(order.totalPrice || order.value || 0).toFixed(2);
  };

  // Get ad groups count from order audiences (for table display)
  const getAdGroups = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      // Get unique ad groups from all audiences
      const groups = [...new Set(order.audiences
        .filter(audience => audience.adGroup)
        .map(audience => parseInt(audience.adGroup)))];
      
      if (groups.length > 0) {
        return groups.length;
      }
    }
    
    // Fall back to 0 if no ad groups found
    return 0;
  };

  // Get ad groups detail list (for detail dialog)
  const getAdGroupsDetail = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      // Get unique ad groups from all audiences
      const groups = [...new Set(order.audiences
        .filter(audience => audience.adGroup)
        .map(audience => parseInt(audience.adGroup)))];
      
      if (groups.length > 0) {
        return groups.sort((a, b) => a - b).map(group => `Group ${group}`).join(', ');
      }
    }
    
    // Fall back to N/A if no ad groups found
    return 'N/A';
  };

  // Get addons from order audiences
  const getAddons = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      const addons = [];
      
      order.audiences.forEach((audience) => {
        if (audience.addons) {
          if (audience.addons.topSpot) {
            addons.push('Top Spot');
          }
          if (audience.addons.gif) {
            addons.push('GIF Animation');
          }
        }
      });
      
      // Remove duplicates and return
      const uniqueAddons = [...new Set(addons)];
      return uniqueAddons.length > 0 ? uniqueAddons.join(', ') : 'None';
    }
    
    return 'None';
  };

  const handleViewDetails = (orderId) => {
    // Find order in ad orders
    const order = adOrders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setDetailDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
  };

  const handleCreateProof = (orderId) => {
    // Check if this is an update order
    const order = [...adOrders, ...updateOrders].find(o => o.id === orderId);
    
    if (order && order.orderType === 'update') {
      // Navigate to update proof creation page
      navigate(`/app/proof/update/${orderId}`);
    } else {
      // Navigate to regular proof creation page
      navigate(`/app/proof/create/${orderId}`);
    }
  };

  const handleCreateQuote = (orderId) => {
    // Navigate directly to quote creation without changing status
    // Status will be changed to "Quoting" when the quote is actually created
    navigate(`/app/quote/create/${orderId}`);
  };

  // Function to check if a quote exists for an order
  const [orderQuotes, setOrderQuotes] = useState({});
  
  const checkQuoteExists = async (orderId) => {
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/quote/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success && response.data.data) {
        setOrderQuotes(prev => ({ ...prev, [orderId]: response.data.data }));
        return response.data.data;
      } else {
        setOrderQuotes(prev => ({ ...prev, [orderId]: null }));
        return null;
      }
    } catch (error) {
      console.error('Error checking quote:', error);
      setOrderQuotes(prev => ({ ...prev, [orderId]: null }));
      return null;
    }
  };

  // Function to handle viewing an existing quote
  const handleViewQuote = async (orderId) => {
    const quote = orderQuotes[orderId];
    
    if (quote && quote.quoteLink) {
      window.open(quote.quoteLink, '_blank');
    } else {
      // Try to fetch quote info again in case it wasn't loaded
      const freshQuote = await checkQuoteExists(orderId);
      if (freshQuote && freshQuote.quoteLink) {
        window.open(freshQuote.quoteLink, '_blank');
      } else {
        console.error('No quote link available for order:', orderId);
        alert('No quote found for this order. Please generate a quote first.');
      }
    }
  };

  // Function to handle canceling a quote from admin interface
  const handleCancelQuote = async (orderId) => {
    const reason = prompt('Optional: Please provide a reason for canceling this quote:');
    
    // User clicked cancel on the prompt
    if (reason === null) {
      return;
    }
    
    if (!window.confirm('Are you sure you want to cancel this quote? This will set the order status to "Changes Needed".')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/quote/cancel/${orderId}`,
        {
          reason: reason || 'Cancelled by admin'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        alert('Quote cancelled successfully. The order status has been set to "Changes Needed".');
        
        // Refresh the orders to show updated status
        await fetchOrders();
      } else {
        console.error('Failed to cancel quote:', response.data.message);
        alert('Failed to cancel quote: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error cancelling quote:', error);
      alert('Error cancelling quote. Please try again.');
    }
  };

  // Check for quotes when orders are loaded
  useEffect(() => {
    const checkAllQuotes = async () => {
      for (const order of adOrders) {
        await checkQuoteExists(order.id);
      }
    };
    
    if (adOrders.length > 0) {
      checkAllQuotes();
    }
  }, [adOrders]);

  // Custom action buttons component for stationery orders
  const StationeryOrderActions = ({ order }) => {
    const status = order.status?.toLowerCase().trim() || 'new';
    const hasQuote = orderQuotes[order.id];
    
    const handleStatusButtonClick = (newStatus) => {
      handleStatusChange(order.id, newStatus, `Status changed to ${newStatus}`);
    };

    const renderActionButtons = () => {
      const buttonStyle = {
        minWidth: 'auto',
        px: 1,
        py: 0.25,
        fontSize: '0.75rem',
        textTransform: 'none'
      };

      switch (status) {
        case 'new':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              {hasQuote ? (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => handleCreateQuote(order.id)}
                  sx={buttonStyle}
                >
                  Update Quote
                </Button>
              ) : (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => handleCreateQuote(order.id)}
                  sx={buttonStyle}
                >
                  Generate Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'quoting':
          return (
            <>
              <Button
                variant="text"
                color="success"
                size="small"
                onClick={() => handleViewQuote(order.id)}
                sx={buttonStyle}
              >
                View Quote
              </Button>
              <Button
                variant="text"
                color="warning"
                size="small"
                onClick={() => handleCancelQuote(order.id)}
                sx={buttonStyle}
              >
                Cancel Quote
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('In Design')}
                sx={buttonStyle}
              >
                Design
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Proofing')}
                sx={buttonStyle}
              >
                Proof
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Approved')}
                sx={buttonStyle}
              >
                Approved
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'in design':
        case 'design':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Proofing')}
                sx={buttonStyle}
              >
                Proof
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Approved')}
                sx={buttonStyle}
              >
                Approved
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'proofing':
        case 'proof':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                disabled
                sx={{ ...buttonStyle, color: 'text.disabled' }}
                title="Functionality coming soon"
              >
                Generate Proof
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Approved')}
                sx={buttonStyle}
              >
                Approved
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'approved':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('In Production')}
                sx={buttonStyle}
              >
                Production
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'in production':
        case 'production':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Invoiced')}
                sx={buttonStyle}
              >
                Invoice
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'accepted':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                color="info"
                size="small"
                onClick={() => navigate(`/app/quote-acceptance-details/${order.id}`)}
                sx={buttonStyle}
              >
                Details
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('In Production')}
                sx={buttonStyle}
              >
                Production
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => handleStatusButtonClick('Invoiced')}
                sx={buttonStyle}
              >
                Invoice
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'invoiced':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'changes needed':
          return (
            <>
              {hasQuote && (
                <Button
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleViewQuote(order.id)}
                  sx={buttonStyle}
                >
                  View Quote
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => handleCreateQuote(order.id)}
                sx={buttonStyle}
              >
                Update Quote
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => handleStatusButtonClick('Cancelled')}
                sx={buttonStyle}
              >
                Cancel
              </Button>
            </>
          );

        case 'cancelled':
          return (
            <Typography variant="caption" color="text.secondary">
              Order Cancelled
            </Typography>
          );

        default:
          return (
            <Typography variant="caption" color="text.secondary">
              No actions available
            </Typography>
          );
      }
    };

    return (
      <Stack direction="row" spacing={1}>
        {renderActionButtons()}
      </Stack>
    );
  };

  const handleStatusChange = async (orderId, newStatus, notes) => {
    // For ad/update orders, skip the 'Proof' status step and go straight to proof creation
    const isAdOrderCheck = adOrders.some(order => order.id === orderId);
    const isUpdateOrderCheck = updateOrders.some(order => order.id === orderId);
    if ((isAdOrderCheck || isUpdateOrderCheck) && newStatus?.toLowerCase() === 'proof' && !notes?._skipProofCreation) {
      handleCreateProof(orderId);
      return;
    }

    // Format the status consistently - capitalize first letter of each word
    const formattedStatus = newStatus
      .split(/[_\s]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Check if order is an ad order or update order
    const isAdOrder = adOrders.some(order => order.id === orderId);
    const isUpdateOrder = updateOrders.some(order => order.id === orderId);
    
    // Store original order for potential rollback
    const originalOrder = isAdOrder 
      ? adOrders.find(order => order.id === orderId)
      : updateOrders.find(order => order.id === orderId);
    
    try {
      if (!isAdOrder && !isUpdateOrder) {
        console.error('Order not found in ad orders or update orders');
        setError('Order not found in the current list');
        return;
      }
      
      // Determine the correct API endpoint
      const apiEndpoint = isUpdateOrder
        ? `${config.apiUrl}/api/update-orders/${orderId}/status`
        : `${config.apiUrl}/api/ad-orders/${orderId}/status`;
      
      // Optimistically update the UI first
      const updateOrderInState = (prevOrders) => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: formattedStatus } 
            : order
        );
      
      if (isAdOrder) {
        // When an order is uploaded it moves to Finance > Unpaid Orders — remove it from this list immediately
        if (formattedStatus.toLowerCase() === 'uploaded') {
          setAdOrders(prev => prev.filter(o => o.id !== orderId));
          if (selectedOrder?.id === orderId) setSelectedOrder(null);
        } else {
          setAdOrders(updateOrderInState);
        }
      } else {
        setUpdateOrders(updateOrderInState);
      }
      
      // If this order is currently selected in the detail view, update it there too
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({
          ...prev,
          status: formattedStatus
        }));
      }
      
      // Call the appropriate API endpoint
      const token = await getToken();
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrfToken = await fetchCSRFToken();
      const response = await axios.post(apiEndpoint, {
        status: formattedStatus,
        notes: notes
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
      
      if (response.data.success) {
        // UI already updated optimistically - no success message needed
      } else {
        throw new Error(response.data.message || 'Status update failed');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError(`Failed to update order status: ${error.response?.data?.message || error.message}`);
      
      // Revert the optimistic update on error
      if (originalOrder) {
        if (isAdOrder) {
          setAdOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId 
                ? originalOrder 
                : order
            )
          );
        } else {
          setUpdateOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId 
                ? originalOrder 
                : order
            )
          );
        }
        
        // Revert selected order if needed
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder(originalOrder);
        }
      }
    }
  };

  // Handle opening notes dialog for any entity
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId: entityId.toString(),
      notes: existingNotes
    });
  };

  // Handle closing notes dialog
  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (entityType, entity) => {
    let dbNoteCount = 0;
    if (entityType === 'order') {
      if (entity.notes) dbNoteCount++;
      if (entity.designNotes) dbNoteCount++;
    }
    
    // Count localStorage notes
    const entityId = entity.id;
    const storageKey = `notes_${entityType}_${entityId}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    return dbNoteCount + storageNotes.length;
  };

  // Handle notes update from the dialog - update local state
  const handleNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    
    // Update the relevant local state to trigger UI refresh
    if (notesDialog.entityType === 'order') {
      // Update both order arrays since we don't know which one contains the order
      setAdOrders(prev => prev.map(order => 
        order.id.toString() === notesDialog.entityId 
          ? { 
              ...order, 
              notes: order.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : order
      ));
    }
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await fetchOrders();
      } catch (error) {
      }
    }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Ad Orders
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Manage and track advertising orders through their workflow
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', mb: 2, mt: 3 }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, newValue) => {
                setActiveTab(newValue);
                localStorage.setItem('orderWorkflowActiveTab', newValue.toString());
              }}>
                <Tab label={`Orders (${adOrders.length})`} />
                <Tab label={`Updates (${updateOrders.length})`} />
              </Tabs>
            </Box>

            {/* Orders Tab */}
            {activeTab === 0 && (
              <>
                {/* Filters */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Grid container spacing={2} alignItems="center">
                {/* Search */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by organization or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Sort By */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort By"
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="organization">Organisation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sort Order */}
                <Grid item xs={12} sm={6} md={2}>
                  <ToggleButtonGroup
                    value={sortOrder}
                    exclusive
                    onChange={(e, newOrder) => {
                      if (newOrder !== null) {
                        setSortOrder(newOrder);
                      }
                    }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="asc" aria-label="ascending">
                      <ArrowUpwardIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="desc" aria-label="descending">
                      <ArrowDownwardIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Filter by Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {getUniqueStatuses(adOrders).map((status) => (
                        <MenuItem key={status} value={status.toLowerCase()}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Advertising Orders Table */}
            <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="advertising orders table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Organisation</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Ad Groups</TableCell>
                      <TableCell>Add-ons</TableCell>
                      <TableCell>Total Price</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getProcessedOrders(adOrders)
                      .map((order) => (
                        <TableRow
                          key={order.id}
                          hover
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell>{order.createdAt ? formatDateLong(order.createdAt) : 'N/A'}</TableCell>
                          <TableCell component="th" scope="row">
                            {order.id}
                          </TableCell>
                          <TableCell>{order.organizationName || order.businessName || 'N/A'}</TableCell>
                          <TableCell>{getChannels(order)}</TableCell>
                          <TableCell>{getAdGroups(order)}</TableCell>
                          <TableCell>
                            {(() => {
                              const addons = getAddons(order);
                              return addons === 'None' ? (
                                <Typography variant="body2" color="text.secondary">None</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {addons.split(', ').map((addon, index) => (
                                    <Chip
                                      key={index}
                                      label={addon}
                                      size="small"
                                      color={addon === 'Top Spot' ? 'warning' : 'info'}
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: '20px' }}
                                    />
                                  ))}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell>${getTotalPrice(order)}</TableCell>
                          <TableCell>
                            <StatusBadge status={order.status || 'Pending'} />
                          </TableCell>
                          <TableCell>
                            <ActionsMenu order={order} />
                          </TableCell>
                        </TableRow>
                      ))}
                    {getProcessedOrders(adOrders).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No advertising orders found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}

            {/* Updates Tab */}
            {activeTab === 1 && (
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="update orders table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Organisation</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Total Price</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {updateOrders.map((order) => (
                      <TableRow
                        key={order.id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>{order.createdAt ? formatDateLong(order.createdAt) : 'N/A'}</TableCell>
                        <TableCell component="th" scope="row">
                          {order.id}
                        </TableCell>
                        <TableCell>{order.organizationName || order.businessName || 'N/A'}</TableCell>
                        <TableCell>{formatUpdateOrderType(order)}</TableCell>
                        <TableCell>${order.totalPrice || order.price || '0.00'}</TableCell>
                        <TableCell>
                          <StatusBadge status={order.status || 'Pending'} />
                        </TableCell>
                        <TableCell>
                          <ActionsMenu order={order} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {updateOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No update orders found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>

      {/* Order Detail Dialog - Use OrderDetail component for better handling */}
      <OrderDetail
        open={detailDialogOpen}
        onClose={handleCloseDialog}
        order={selectedOrder}
        onUpdate={(updatedOrder) => {
          // Refresh the orders list
          fetchOrders();
        }}
      />

      {/* Notes Dialog - Order Workflow Version */}
      <OrderWorkflowNotesDialog
        open={notesDialog.open}
        onClose={handleCloseNotesDialog}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
        onNotesUpdate={handleNotesUpdate}
      />
    </Container>
  );
};

export default OrderWorkflow; 